import React, { useMemo, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  Select,
  MenuItem,
  Button,
  Chip,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import deviceTypeStore from "../../stores/DeviceTypeStore";

const renderBoldHeader = (params) => (
  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
    {params?.colDef?.headerName || ""}
  </Typography>
);

const DefaultConfigTab = observer(() => {
  const navigate = useNavigate();
  const deviceTypes = deviceTypeStore.deviceTypes;
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Device Type ID",
        width: 300,
        renderHeader: renderBoldHeader,
      },
      {
        field: "type_name",
        headerName: "Device Type Name",
        width: 220,
        renderHeader: renderBoldHeader,
      },
      {
        field: "manufacture",
        headerName: "Manufacture",
        width: 200,
        renderHeader: renderBoldHeader,
      },
      {
        field: "status",
        headerName: "Status",
        width: 180,
        renderHeader: renderBoldHeader,
        renderCell: (params) => {
          const isConfigured = !!params.row.default_config_id;

          return (
            <Chip
              icon={
                isConfigured ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />
              }
              label={isConfigured ? "Configured" : "Not Configured"}
              color={isConfigured ? "success" : "warning"}
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: "0.85rem",
                px: 1.5,
                borderRadius: 1.5,
                "& .MuiChip-icon": {
                  fontSize: "1.2rem",
                },
              }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: renderBoldHeader,
        renderCell: (params) => (
          <Tooltip title="View Config">
            <IconButton
              onClick={() =>
                navigate(`/alerts/default-config/${params.row.id}`, {
                  state: { deviceTypeRecord: params.row },
                })
              }
              size="small"
              sx={{ color: theme.palette.primary.main }}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate, theme.palette.primary.main]
  );

  const rows = useMemo(
    () =>
      deviceTypes.map((deviceType) => ({
        id: deviceType.id,
        type_name: deviceType.type_name || "Unknown",
        manufacture: deviceType.manufacture || "Unknown",
        alert_config_id: deviceType.default_config_id,
      })),
    [deviceTypes]
  );

  const totalPages = Math.max(Math.ceil(rows.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = rows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const buildPageNumbers = useCallback((total, current) => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const range = [1];
    if (current > 3) range.push("...");
    for (
      let i = Math.max(2, current - 1);
      i <= Math.min(total - 1, current + 1);
      i++
    ) {
      range.push(i);
    }
    if (current < total - 2) range.push("...");
    range.push(total);
    return range;
  }, []);

  const pageNumbers = useMemo(
    () => buildPageNumbers(totalPages, currentPage),
    [totalPages, currentPage]
  );

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Box sx={{ height: 540, width: "100%" }}>
        <DataGridPro
          rows={paginatedRows}
          columns={columns}
          disableColumnMenu
          disableSelectionOnClick
          pagination={false}
          hideFooter={true}
          sx={{
            borderRadius: 2,
            boxShadow: 2,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f4f6f8",
              fontWeight: "bold",
              fontSize: "0.9rem",
              borderBottom: "1px solid #ddd",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
              color: "#333",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#ffffff",
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#f7fbff",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#e3f2fd",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
        />
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
        }}
      >
        <Button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Prev
        </Button>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {pageNumbers.map((p, i) => (
            <Button
              key={i}
              onClick={() => setPage(p)}
              variant={p === currentPage ? "contained" : "text"}
              disabled={p === "..."}
            >
              {p}
            </Button>
          ))}
        </Box>
        <Button
          onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography>Rows per page:</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </Box>
  );
});

export default DefaultConfigTab;
